// @ts-expect-error
import scrollToWithAnimation from 'scrollto-with-animation';

import MiniLoader from '@payaca/components/miniLoader/MiniLoader';
import Tooltip from '@payaca/components/tooltip/Tooltip';
import UntitledIcon from '@payaca/untitled-icons';
import { clsx } from '@payaca/utilities/cvaMerge';
import React, {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';
import './DashboardActionRequiredCarousel.sass';

type Props = {
  actionRequiredItems: React.ReactNode[] | undefined;
  title?: string | React.ReactNode;
  description?: string;
  isLoading?: boolean;
};
const DashboardActionRequiredCarousel: FC<Props> = ({
  actionRequiredItems,
  title,
  description,
  isLoading = false,
}: Props): JSX.Element => {
  const horizontalScrollContainerElement = useRef<HTMLDivElement>(null);
  const itemsContainerElement = useRef<HTMLDivElement>(null);
  const [scrollArrow, setScrollArrow] = useState<'left' | 'right' | false>(
    false
  );
  const [scrollInterval, setScrollInterval] =
    useState<ReturnType<typeof setTimeout>>();
  const [scrollOffset, setScrollOffset] = useState(0);

  const [horizontalScrollContainerWidth, setHorizontalScrollContainerWidth] =
    useState(0);

  const [itemsContainerWidth, setItemsContainerWidth] = useState(0);

  useLayoutEffect(() => {
    setHorizontalScrollContainerWidth(
      horizontalScrollContainerElement?.current?.offsetWidth || 0
    );
    setItemsContainerWidth(itemsContainerElement?.current?.scrollWidth || 0);
  }, [
    itemsContainerElement?.current?.scrollWidth,
    horizontalScrollContainerElement?.current?.offsetWidth,
    actionRequiredItems,
  ]);

  const scrollRequired = useMemo(() => {
    return itemsContainerWidth > horizontalScrollContainerWidth;
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: actionRequiredItems
  }, [
    horizontalScrollContainerWidth,
    itemsContainerWidth,
    actionRequiredItems,
  ]);

  const scrollSegment = useCallback(
    (scrollArrow: 'left' | 'right', easing = 'linearTween') => {
      if (!itemsContainerElement?.current) return;

      const currentScroll = itemsContainerElement.current.scrollLeft || 0;
      const newScrollPosition =
        currentScroll + (scrollArrow === 'right' ? 100 : -100);
      scrollToWithAnimation(
        itemsContainerElement.current, // element to scroll
        'scrollLeft', // direction to scroll
        newScrollPosition, // target scrollX
        100, // duration in ms
        easing
      );
      setScrollOffset(newScrollPosition);
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has an unnecessary dependency: itemsContainerElement.
    [itemsContainerElement?.current]
  );

  useEffect(() => {
    if (scrollArrow && itemsContainerElement?.current) {
      scrollSegment(scrollArrow, 'easeInCirc');
      setScrollInterval(
        setInterval(() => {
          scrollSegment(scrollArrow);
        }, 100)
      );
    } else {
      setScrollInterval((x) => {
        if (x) {
          clearInterval(x);
        }
        return undefined;
      });
    }
  }, [scrollArrow, scrollSegment]);

  return (
    <div className="dashboard-action-required-carousel">
      {title && (
        <div className="title-container flex-container flex-center">
          <div className="flex-container flex-contiguous flex-center">
            {typeof title === 'string' ? <h2>{title}</h2> : title}
            {description && <Tooltip text={description} />}
          </div>
          {isLoading && <MiniLoader />}
        </div>
      )}
      {!!actionRequiredItems?.length && (
        <div
          className="horizontal-scroll-container"
          ref={horizontalScrollContainerElement}
        >
          {scrollRequired && scrollOffset > 0 && (
            <ScrollSegment scrollArrow="left" setScrollArrow={setScrollArrow} />
          )}
          <div
            className="flex-container flex-center items-container"
            ref={itemsContainerElement}
          >
            {actionRequiredItems}
            {scrollRequired && <div className="spacer" />}
          </div>
          {scrollRequired && (
            <ScrollSegment
              scrollArrow="right"
              setScrollArrow={setScrollArrow}
            />
          )}
        </div>
      )}
    </div>
  );
};

type ScrollSegmentProps = {
  scrollArrow: 'left' | 'right';
  setScrollArrow: (scrollArrow: 'left' | 'right' | false) => void;
};
const ScrollSegment: FC<ScrollSegmentProps> = ({
  scrollArrow,
  setScrollArrow,
}) => {
  return (
    <div
      className={clsx(
        'absolute top-0 bottom-0 flex z-10',
        scrollArrow === 'left' ? 'left-0 flex-row-reverse' : 'right-0 flex-row'
      )}
    >
      <div
        className={clsx(
          'w-[50px] h-full from-transparent to-white',
          scrollArrow === 'left' ? 'bg-gradient-to-l' : 'bg-gradient-to-r'
        )}
      />
      <div
        className="w-[30px] flex items-center justify-center bg-white cursor-pointer"
        onMouseDown={() => setScrollArrow(scrollArrow)}
        onMouseUp={() => setScrollArrow(false)}
        onMouseLeave={() => setScrollArrow(false)}
      >
        <UntitledIcon
          name={scrollArrow === 'left' ? 'chevron-left' : 'chevron-right'}
          className="size-4 text-blue"
        />
      </div>
    </div>
  );
};

export default DashboardActionRequiredCarousel;
